import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Code, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PageIssue, LowTextRatioDetails } from "@/types/technicalAudit";

interface LowTextRatioCardProps {
	issue: PageIssue;
}

function getRatioColor(ratio: number): { bar: string; text: string } {
	if (ratio >= 25) return { bar: "bg-green-500", text: "text-green-700 dark:text-green-400" };
	if (ratio >= 10) return { bar: "bg-amber-500", text: "text-amber-700 dark:text-amber-400" };
	return { bar: "bg-red-500", text: "text-red-700 dark:text-red-400" };
}

export function LowTextRatioCard({ issue }: LowTextRatioCardProps) {
	const details = issue.issue_details as LowTextRatioDetails;
	const colors = getRatioColor(details.text_ratio);
	const htmlSizeKB = (details.html_bytes / 1024).toFixed(0);

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
					<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-amber-900 dark:text-amber-300">Low Text-to-HTML Ratio</h4>
					<p className="text-sm mt-1 text-amber-700 dark:text-amber-400">
						Only {details.text_ratio}% of this page is visible text. Pages with low text ratio may be seen as low-quality by search engines.
					</p>
				</div>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Code className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Text Ratio
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-4">
					<div className="flex items-center justify-between">
						<span className={cn("text-2xl font-bold", colors.text)}>
							{details.text_ratio}%
						</span>
						<span className="text-sm text-muted-foreground">
							HTML size: {htmlSizeKB} KB
						</span>
					</div>
					<div className="w-full h-3 bg-muted rounded-full overflow-hidden">
						<div
							className={cn("h-full rounded-full transition-all", colors.bar)}
							style={{ width: `${Math.min(details.text_ratio, 100)}%` }}
						/>
					</div>
					<div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
						<span className="flex items-center gap-1">
							<span className="w-2 h-2 rounded-full bg-red-500" />
							&lt;10%
						</span>
						<span className="flex items-center gap-1">
							<span className="w-2 h-2 rounded-full bg-amber-500" />
							10-25%
						</span>
						<span className="flex items-center gap-1">
							<span className="w-2 h-2 rounded-full bg-green-500" />
							25%+
						</span>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
